import { RoleId, RoleName } from '@common/constants';
import {
    Button,
    Checkbox,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControl,
    FormControlLabel,
    FormLabel,
    Radio,
    RadioGroup,
    TextField,
    Typography,
} from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';
import { type AxiosError, isAxiosError } from 'axios';
import { type ChangeEvent, useState } from 'react';
import z from 'zod';
import LoadingButton from '@/components/Buttons/LoadingButton';
import ReadOnlyTextField from '@/components/ReadOnlyTextField';
import { createGraphUser } from '@/services/UserService';
import { errorSnackbar, successSnackbar, warningSnackbar } from '@/utils/snackbar';

const EMAIL_SCHEMA = z.email('Please enter a valid email address');

type Role = typeof RoleName.LAWYER | typeof RoleName.CLERK | typeof RoleName.NOTARY;

interface FormData {
    email: string;
    firstName: string;
    lastName: string;
    role: Role;
    isOrgAdmin: boolean;
}

interface FormErrors {
    email: string;
    firstName: string;
    lastName: string;
    role: string;
}

interface Props {
    lawFirmId: number;
    lawFirmName?: string;
    onClose(): void;
}

export default function CreateAccountDialog({ lawFirmId, lawFirmName, onClose }: Props) {
    const queryClient = useQueryClient();

    const [form, setForm] = useState<FormData>({
        email: '',
        firstName: '',
        lastName: '',
        role: RoleName.LAWYER,
        isOrgAdmin: false,
    });

    const [errors, setErrors] = useState<FormErrors>({
        email: '',
        firstName: '',
        lastName: '',
        role: '',
    });

    const [isCreating, setIsCreating] = useState(false);

    const validateEmail = (value: string): boolean => {
        if (!value.trim()) {
            setErrors((prev) => ({ ...prev, email: 'Email is required' }));
            return false;
        }
        const result = EMAIL_SCHEMA.safeParse(value.trim());
        if (!result.success) {
            setErrors((prev) => ({ ...prev, email: result.error.issues[0].message }));
            return false;
        }
        setErrors((prev) => ({ ...prev, email: '' }));
        return true;
    };

    const validateRequired = (value: string, field: keyof FormErrors, message: string): boolean => {
        if (!value.trim()) {
            setErrors((prev) => ({ ...prev, [field]: message }));
            return false;
        }
        setErrors((prev) => ({ ...prev, [field]: '' }));
        return true;
    };

    const handleTextChange = (field: keyof FormData) => (e: ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        setForm((prev) => ({ ...prev, [field]: value }));

        if (errors[field as keyof FormErrors] && value.trim()) {
            if (field === 'email') {
                validateEmail(value);
            } else {
                validateRequired(value, field as keyof FormErrors, `${field} is required`);
            }
        }
    };

    const handleRoleChange = (e: ChangeEvent<HTMLInputElement>) => {
        setForm((prev) => ({ ...prev, role: e.target.value as Role }));
        setErrors((prev) => ({ ...prev, role: '' }));
    };

    const handleOrgAdminChange = (e: ChangeEvent<HTMLInputElement>) => {
        setForm((prev) => ({ ...prev, isOrgAdmin: e.target.checked }));
    };

    const validateForm = (): boolean => {
        const emailValid = validateEmail(form.email);
        const firstNameValid = validateRequired(
            form.firstName,
            'firstName',
            'First name is required',
        );
        const lastNameValid = validateRequired(form.lastName, 'lastName', 'Last name is required');

        // Role is required — we give it a default but still validate on submit
        if (!form.role) {
            setErrors((prev) => ({ ...prev, role: 'Role is required' }));
        }

        return emailValid && firstNameValid && lastNameValid && !!form.role;
    };

    const handleCreateUser = async () => {
        if (!validateForm()) return;

        setIsCreating(true);

        try {
            const roleIds: RoleId[] = [RoleId.BASIC];
            if (form.role === RoleName.LAWYER) {
                roleIds.push(RoleId.LAWYER);
            } else if (form.role === RoleName.CLERK) {
                roleIds.push(RoleId.CLERK);
            } else if (form.role === RoleName.NOTARY) {
                roleIds.push(RoleId.NOTARY);
            }
            if (form.isOrgAdmin) {
                roleIds.push(RoleId.ORGANIZATIONAL_ADMIN);
            }

            await createGraphUser({
                email: form.email.trim().toLowerCase(),
                firstName: form.firstName.trim(),
                lastName: form.lastName.trim(),
                lawFirmId,
                roleIds,
            });

            await queryClient.invalidateQueries({ queryKey: ['users'] });
            successSnackbar('User created successfully!');
            onClose();
        } catch (error: unknown) {
            console.error('Error creating user:', error);
            if (isAxiosError(error) && error.response?.status === 409) {
                warningSnackbar('A user with this email address already exists.');
            } else {
                const axiosError = error as AxiosError<string>;
                const errorMessage =
                    axiosError.response?.data || 'Failed to create user. Please try again.';
                errorSnackbar(errorMessage);
            }
        } finally {
            setIsCreating(false);
        }
    };

    const isFormValid =
        form.email.trim() &&
        form.firstName.trim() &&
        form.lastName.trim() &&
        form.role &&
        !Object.values(errors).some(Boolean);

    return (
        <Dialog
            open
            onClose={(_event, reason) => {
                if (reason === 'backdropClick' && isCreating) return;
                onClose();
            }}
        >
            <DialogTitle>Create User</DialogTitle>
            <DialogContent>
                <Typography variant="body2" sx={{ mb: 3, color: '#7B7D80' }}>
                    Enter the details for the new user. They will receive an email with their
                    account credentials.
                </Typography>
                <ReadOnlyTextField label="Law firm" value={lawFirmName} fullWidth />
                <TextField
                    autoFocus
                    margin="dense"
                    label="Email"
                    type="email"
                    fullWidth
                    variant="outlined"
                    value={form.email}
                    onChange={handleTextChange('email')}
                    onBlur={() => form.email.trim() && validateEmail(form.email)}
                    disabled={isCreating}
                    placeholder="Enter user email"
                    error={!!errors.email}
                    helperText={errors.email}
                />
                <TextField
                    margin="dense"
                    label="First Name"
                    fullWidth
                    variant="outlined"
                    value={form.firstName}
                    onChange={handleTextChange('firstName')}
                    onBlur={() =>
                        form.firstName.trim() &&
                        validateRequired(form.firstName, 'firstName', 'First name is required')
                    }
                    disabled={isCreating}
                    placeholder="Enter first name"
                    error={!!errors.firstName}
                    helperText={errors.firstName}
                />
                <TextField
                    margin="dense"
                    label="Last Name"
                    fullWidth
                    variant="outlined"
                    value={form.lastName}
                    onChange={handleTextChange('lastName')}
                    onBlur={() =>
                        form.lastName.trim() &&
                        validateRequired(form.lastName, 'lastName', 'Last name is required')
                    }
                    disabled={isCreating}
                    placeholder="Enter last name"
                    error={!!errors.lastName}
                    helperText={errors.lastName}
                />
                <FormControl fullWidth component="fieldset" sx={{ mt: 2 }} error={!!errors.role}>
                    <FormLabel component="legend">Role</FormLabel>
                    <RadioGroup row value={form.role} onChange={handleRoleChange}>
                        <FormControlLabel
                            value={RoleName.LAWYER}
                            control={<Radio disabled={isCreating} />}
                            label={RoleName.LAWYER}
                        />
                        <FormControlLabel
                            value={RoleName.CLERK}
                            control={<Radio disabled={isCreating} />}
                            label={RoleName.CLERK}
                        />
                        <FormControlLabel
                            value={RoleName.NOTARY}
                            control={<Radio disabled={isCreating} />}
                            label={RoleName.NOTARY}
                        />
                    </RadioGroup>
                    {errors.role && (
                        <Typography variant="caption" color="error">
                            {errors.role}
                        </Typography>
                    )}
                </FormControl>
                <FormControlLabel
                    control={
                        <Checkbox
                            checked={form.isOrgAdmin}
                            onChange={handleOrgAdminChange}
                            disabled={isCreating}
                        />
                    }
                    label="Organizational Admin"
                />
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} disabled={isCreating}>
                    Cancel
                </Button>
                <LoadingButton
                    onClick={handleCreateUser}
                    loading={isCreating}
                    variant="contained"
                    disabled={!isFormValid || isCreating}
                >
                    Create
                </LoadingButton>
            </DialogActions>
        </Dialog>
    );
}
